import { Injectable, OnDestroy } from '@angular/core';
import getDay from 'date-fns/getDay';
import { BehaviorSubject, firstValueFrom, map, Subject } from 'rxjs';
import { AvailabilityListUser } from 'server/shared/models/availabilityListUser';
import { Department } from 'server/shared/models/department';
import { ShiftEnum } from 'server/shared/models/shift.enum';
import { PoolerUser } from 'server/shared/models/user';
import { getWeekDayEnumByIndex } from 'server/shared/models/weekday.enum';
import { OrganisationService } from 'src/app/services/organisation.service';
import { getSecondsToJobStart } from '../../../../helpers/getSecondsToJobStart';
import {
  MultiDaySelectionSummary,
  UserShiftSelection,
} from '../../../../types/job/MultiDaySelectionSummary';
import { CustomerService } from '../../../services/customer.service';
import { PoolerService } from '../../../services/pooler/pooler.service';

export type SelectedAvailabilityData = {
  department: Department;
  user: AvailabilityListUser;
  shiftEnum: ShiftEnum;
  specificShiftId?: string;
  note?: string;
  date: string;
};

export type MultiJobCreationSelection = {
  departmentId: string;
  selection: Map<string, UserShiftSelection>;
};

@Injectable()
export class AvailabilitySelectionService implements OnDestroy {
  private destroyed$ = new Subject<void>();

  multiCreationSelection$ = new BehaviorSubject<
    MultiJobCreationSelection | undefined
  >(undefined);

  selectionSummary$ = this.multiCreationSelection$.pipe(
    map((selection): MultiDaySelectionSummary | undefined => {
      if (!selection) return undefined;
      const canBookUsers: UserShiftSelection[] = [];
      const inviteUsers: UserShiftSelection[] = [];

      selection.selection.forEach((userShiftSelection) => {
        if (userShiftSelection.canBookDirectly) {
          canBookUsers.push(userShiftSelection);
        } else {
          inviteUsers.push(userShiftSelection);
        }
      });

      return {
        departmentId: selection.departmentId,
        canBookUsers,
        inviteUsers,
      };
    }),
  );

  constructor(
    private poolerService: PoolerService,
    private organisationService: OrganisationService,
    private customerService: CustomerService,
  ) {}

  resetSelection() {
    this.multiCreationSelection$.next(undefined);
  }

  getSelectionMapKey(user: PoolerUser, shiftEnum: ShiftEnum, date: string) {
    return `${user._id}-${shiftEnum}-${date}`;
  }

  async selectAvailability(
    newSelection: SelectedAvailabilityData,
  ): Promise<void> {
    // adjust selection
    const selection: MultiJobCreationSelection = this.multiCreationSelection$
      .value || {
      departmentId: newSelection.department.id,
      selection: new Map<string, UserShiftSelection>(),
    };
    const weekDay = getWeekDayEnumByIndex(getDay(new Date(newSelection.date)));

    const shift = newSelection.specificShiftId
      ? newSelection.department.shifts.find(
          (shift) => shift.id === newSelection.specificShiftId,
        )
      : newSelection.department.shifts.find(
          (shift) =>
            shift.shift === newSelection.shiftEnum &&
            (!shift.specificWeekDays ||
              shift.specificWeekDays.length === 0 ||
              shift.specificWeekDays.includes(weekDay)),
        );
    if (!shift)
      throw new Error(
        'shift not found on department' +
          (newSelection.specificShiftId ? 'with specific id' : ''),
      );
    const key = this.getSelectionMapKey(
      newSelection.user,
      shift.shift,
      newSelection.date,
    );
    if (selection.selection.has(key)) {
      selection.selection.delete(key);
    } else {
      let secondsToJobStart = getSecondsToJobStart(
        newSelection.date,
        shift.from,
      );
      const customerOfPooler = await this.poolerService.getCustomerOfPooler(
        newSelection.user._id,
      );
      if (!customerOfPooler)
        throw new Error(
          'customerOfPooler not found for multi creation selection',
        );

      let departmentAndOrganisation =
        await this.organisationService.getOrganisationAndDepartmentById(
          newSelection.department.id,
        );
      if (!departmentAndOrganisation)
        throw new Error('departmentAndOrganisation not found');
      let { department } = departmentAndOrganisation;

      let preventDirectAcceptWithinSecondsPerShift = 0;

      if (
        department?.customerInformation?.[customerOfPooler._id]
          ?.preventDirectAcceptWithinSecondsPerShift?.[shift.shift] !==
        undefined
      ) {
        preventDirectAcceptWithinSecondsPerShift =
          department?.customerInformation?.[customerOfPooler._id]
            ?.preventDirectAcceptWithinSecondsPerShift?.[shift.shift] || 0;
      } else if (
        customerOfPooler.preventDirectAcceptWithinSecondsPerShift?.[
          shift.shift
        ] !== undefined
      ) {
        preventDirectAcceptWithinSecondsPerShift =
          customerOfPooler.preventDirectAcceptWithinSecondsPerShift?.[
            shift.shift
          ];
      }

      // Check if this is a shared pooler and if direct booking is prevented
      const loggedInCustomer = await firstValueFrom(
        this.customerService.readyDispoCustomer$,
      );
      const isSharedPooler =
        loggedInCustomer && customerOfPooler._id !== loggedInCustomer._id;
      const canDirectBookFromSharedPool =
        !isSharedPooler ||
        !customerOfPooler.preventSharedPoolDirectAcceptCustomerIds ||
        !customerOfPooler.preventSharedPoolDirectAcceptCustomerIds.includes(
          loggedInCustomer?._id || '',
        );

      const canBookDirectly =
        canDirectBookFromSharedPool &&
        (!preventDirectAcceptWithinSecondsPerShift ||
          secondsToJobStart > preventDirectAcceptWithinSecondsPerShift);
      selection.selection.set(key, {
        user: newSelection.user,
        shift,
        date: newSelection.date,
        canBookDirectly,
      });
    }
    this.multiCreationSelection$.next(selection);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
